import React, { useEffect, useState } from 'react';
import {
  fetchSessionOrderReport,
  fetchSessionHubStatuses,
  recordOrderingHubEvent,
  listPurchaseOrdersForCountSession,
  fetchLastDraftCreateFailure,
} from '../../services/orderingHubService';
import { BTN_PRIMARY, BTN_SECONDARY, FOCUS_RING_CSS } from './orderingStyles';
import {
  buildPurchaseOrdersFromReport,
  sendPurchaseOrdersBatch,
  createDraftPurchaseOrdersFromCount,
  deleteDraftPurchaseOrder,
  updateDraftFromCount,
  amendSentFromCount,
} from '../../services/purchaseOrderService';
import { printPurchaseOrder } from '../orders/printPurchaseOrder';
import { printReportDocument } from '../reports/ReportDriver';
import { getCurrentUser } from '../../utils/userDisplay';
import { describeDraftCreationOutcome } from '../../utils/countCompletionWorkflow';

const fmtMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '$0.00';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

// A quantity we do not have is a dash, not a zero. "0 on hand" and "we did not
// count it" order very different amounts of stock.
// A price we do not have is not $0.00 — $0.00 tells a vendor the item is free
// and tells a buyer the line is priced. `unitPrice`/`orderValue` are NULL for a
// line the count could not price (see _shared/countOrderLine.mjs), and this is
// where that difference has to become visible.
const PRICE_UNAVAILABLE = 'Price needed';
const fmtMoneyOrMissing = (value) => (value === null || value === undefined ? PRICE_UNAVAILABLE : fmtMoney(value));

const fmtQty = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

// A quantity with the unit it is measured in. On Hand, PAR and Order are three
// numbers that are NOT necessarily in the same unit — an order is placed in the
// vendor's packages while a count is in the item's own unit — so a bare column
// of figures invites them to be read as comparable when they are not.
const fmtQtyUnit = (value, unit) => {
  const text = fmtQty(value);
  if (text === '—' || !unit) return text;
  return `${text} ${unit}`;
};

const formatFailureWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

export default function CountOrderReview({
  session,
  storeId,
  storeName,
  storeAddress = '',
  userEmail = '',
  userName = '',
  isMulti,
  reviewIndex,
  reviewTotal,
  onNext,
  onCancel,
  onOpenDraft,
}) {
  const [vendorData, setVendorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [actionState, setActionState] = useState({ sending: false, generating: false, printing: false, creatingDraft: false });
  const [actionFeedback, setActionFeedback] = useState('');
  const [actionError, setActionError] = useState('');
  const [auditWarning, setAuditWarning] = useState('');
  // Delivery date/notes are stored per vendor, keyed by vendorKey() so each
  // PO generated/sent from this session can carry its own delivery instructions.
  const [vendorDeliveryInfo, setVendorDeliveryInfo] = useState({});
  // Track whether a PO has been generated for this session (from prior audit
  // log or set to true when generated in this browser session).
  const [hasPo, setHasPo] = useState(false);
  // Existing draft/sent purchase orders linked to this count session. Used to
  // decide whether to surface the "Create Draft Order" retry button (shown
  // only when this session has no associated PO yet — e.g. when draft creation
  // failed at the moment of count completion).
  const [existingPos, setExistingPos] = useState([]);
  // Most-recent recorded 'draft_create_failed' breadcrumb, if any. Surfaces
  // the underlying error reason (timeout, RLS, malformed snapshot, etc.) next
  // to the "Create Draft Order" retry button so users / support can diagnose
  // why automatic draft creation didn't happen at count-completion time.
  const [lastDraftFailure, setLastDraftFailure] = useState(null);
  // When mobile drafts are found after loading, show a picker screen first
  // so the user consciously chooses between the mobile draft and a new web PO.
  const [draftPickerDismissed, setDraftPickerDismissed] = useState(false);

  // Confirmation modal state — shown before any action when existing POs exist.
  // pendingAction: 'send' | 'generate' | 'draft'
  // drafts: existing draft POs for this session
  // sentPos: existing sent/active POs for this session
  const [existingOrdersModal, setExistingOrdersModal] = useState(null);
  const [existingOrdersWorking, setExistingOrdersWorking] = useState(false);
  const [existingOrdersError, setExistingOrdersError] = useState('');
  // Full amendment draft summaries returned after amend path, for linking the user to them.
  // Each entry: { amendmentDraftId, amendmentDraftPoNumber, vendorName, parentPoNumber }
  const [amendmentDraftIds, setAmendmentDraftIds] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setReviewed(false);
    setActionFeedback('');
    setActionError('');
    setHasPo(false);
    setVendorDeliveryInfo({});
    setExistingPos([]);
    setLastDraftFailure(null);
    setExistingOrdersModal(null);
    setAmendmentDraftIds([]);
    Promise.all([
      fetchSessionOrderReport(session.id),
      fetchSessionHubStatuses([session.id]),
      listPurchaseOrdersForCountSession(session.id).catch(() => []),
      fetchLastDraftCreateFailure(session.id).catch(() => null),
    ])
      .then(([data, hubStatuses, pos, lastFailure]) => {
        if (cancelled) return;
        setVendorData(data || []);
        setHasPo(Boolean(hubStatuses?.[session.id]?.hasGeneratePo));
        setExistingPos(pos || []);
        setLastDraftFailure(lastFailure || null);
        setLoading(false);
        setReviewed(true);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load order report.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [session.id]);

  const totalValue = vendorData.reduce((s, v) => s + (Number(v.totalOrderValue) || 0), 0);
  const totalLines = vendorData.reduce((s, v) => s + (v.items?.length || 0), 0);
  const isEmpty = vendorData.length === 0;

  const vendorKey = (vendor, idx = 0) => (
    vendor?.vendorId
      || (vendor?.vendorName ? `name:${vendor.vendorName}` : `idx:${idx}`)
  );

  const getVendorDelivery = (vendor, idx = 0) => (
    vendorDeliveryInfo[vendorKey(vendor, idx)] || { requestedDeliveryDate: '', deliveryNotes: '' }
  );

  const updateVendorDelivery = (vendor, idx, patch) => {
    const key = vendorKey(vendor, idx);
    setVendorDeliveryInfo((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || { requestedDeliveryDate: '', deliveryNotes: '' }), ...patch },
    }));
  };

  // Attach each vendor's own requestedDeliveryDate/deliveryNotes onto the
  // vendorOrder objects so buildPurchaseOrder picks them up per PO.
  const enrichVendorOrdersWithDelivery = () => vendorData.map((vendor, idx) => {
    const info = getVendorDelivery(vendor, idx);
    return {
      ...vendor,
      requestedDeliveryDate: info.requestedDeliveryDate || null,
      deliveryNotes: info.deliveryNotes || null,
    };
  });

  const currentUser = getCurrentUser();
  const actor = {
    name: currentUser?.compactName || userName || userEmail || 'User',
    email: userEmail,
  };

  // Classify existing POs into draft vs active-sent buckets.
  // Terminal statuses (canceled, voided, received, etc.) are excluded.
  const DRAFT_STATUSES_SET = new Set(['draft', 'blocked_no_email']);
  const TERMINAL_STATUSES_SET = new Set(['canceled', 'voided', 'received', 'closed', 'replaced', 'revised']);
  const classifyExistingPos = () => {
    const drafts = existingPos.filter(
      (po) => DRAFT_STATUSES_SET.has(po.delivery_status) && !po.archived_at,
    );
    const sentPos = existingPos.filter(
      (po) => !DRAFT_STATUSES_SET.has(po.delivery_status)
            && !TERMINAL_STATUSES_SET.has(po.delivery_status)
            && !po.archived_at,
    );
    return { drafts, sentPos };
  };

  // Open the confirmation modal if any active orders exist for this session.
  // Returns true if the modal was opened (caller should abort its action).
  const interceptWithExistingOrdersModal = (pendingAction) => {
    const { drafts, sentPos } = classifyExistingPos();
    if (drafts.length === 0 && sentPos.length === 0) return false;
    setExistingOrdersModal({ pendingAction, drafts, sentPos });
    setExistingOrdersError('');
    setAmendmentDraftIds([]);
    return true;
  };

  // Execute the confirmed action chosen in the existing-orders modal.
  const handleExistingOrdersConfirm = async (choice) => {
    // choice: 'update' | 'amend'
    if (!existingOrdersModal) return;
    setExistingOrdersWorking(true);
    setExistingOrdersError('');
    setAmendmentDraftIds([]);
    try {
      const { drafts, sentPos, pendingAction } = existingOrdersModal;

      if (choice === 'update' && drafts.length > 0) {
        await updateDraftFromCount({ countSessionId: session.id });
        const refreshed = await listPurchaseOrdersForCountSession(session.id).catch(() => []);
        setExistingPos(refreshed || []);
        setExistingOrdersModal(null);
        await record('hub_update_draft_from_count', { poIds: drafts.map((d) => d.id), vendorCount: drafts.length });
        setActionFeedback(
          `Updated ${drafts.length} draft order${drafts.length === 1 ? '' : 's'} with current count values. You can now find ${drafts.length === 1 ? 'it' : 'them'} in the Drafts tab to review and send.`
        );
      } else if (choice === 'amend' && sentPos.length > 0) {
        const sentPoIds = sentPos.map((po) => po.id);
        const result = await amendSentFromCount({ countSessionId: session.id, sentPoIds });
        const amendSummaries = result?.data?.amendmentDrafts || [];
        setAmendmentDraftIds(amendSummaries);
        const refreshed = await listPurchaseOrdersForCountSession(session.id).catch(() => []);
        setExistingPos(refreshed || []);
        await record('hub_amend_sent_from_count', { sentPoIds, amendmentDraftIds: amendSummaries.map((d) => d.amendmentDraftId) });
        const amendCount = amendSummaries.length;
        setExistingOrdersModal(null);
        setActionFeedback(
          `Created ${amendCount} amendment draft${amendCount === 1 ? '' : 's'} with current count values. Review and send ${amendCount === 1 ? 'it' : 'them'} from the Drafts tab.`
        );
      } else if (choice === 'both') {
        // Mixed: update drafts + amend sent
        const updPromise = drafts.length > 0 ? updateDraftFromCount({ countSessionId: session.id }) : Promise.resolve();
        const amendsPromise = sentPos.length > 0
          ? amendSentFromCount({ countSessionId: session.id, sentPoIds: sentPos.map((po) => po.id) })
          : Promise.resolve(null);
        const [, amendResult] = await Promise.all([updPromise, amendsPromise]);
        const amendSummaries = amendResult?.data?.amendmentDrafts || [];
        setAmendmentDraftIds(amendSummaries);
        const refreshed = await listPurchaseOrdersForCountSession(session.id).catch(() => []);
        setExistingPos(refreshed || []);
        await record('hub_update_and_amend_from_count', {
          updatedPoIds: drafts.map((d) => d.id),
          sentPoIds: sentPos.map((po) => po.id),
          amendmentDraftIds: amendSummaries.map((d) => d.amendmentDraftId),
        });
        setExistingOrdersModal(null);
        setActionFeedback(
          `Updated ${drafts.length} draft${drafts.length === 1 ? '' : 's'} and created ${amendSummaries.length} amendment draft${amendSummaries.length === 1 ? '' : 's'} with current count values. Review and send from the Drafts tab.`
        );
      }
    } catch (err) {
      setExistingOrdersError(err?.message || 'Action failed. Please try again.');
    } finally {
      setExistingOrdersWorking(false);
    }
  };

  const record = async (action, extra = {}) => {
    const result = await recordOrderingHubEvent({
      action,
      countSessionId: session.id,
      actorEmail: actor.email,
      details: { sessionName: session.name, ...extra },
    });
    if (result && !result.success) {
      setAuditWarning(`Activity log could not be saved for "${action}". Your action completed, but the record may be missing.`);
    }
    return result;
  };

  const handleSendToVendor = async () => {
    if (!reviewed || isEmpty) return;
    if (interceptWithExistingOrdersModal('send')) return;
    setActionState((s) => ({ ...s, sending: true }));
    setActionFeedback('');
    setActionError('');
    try {
      const sourceMetadata = {
        storeId,
        storeName,
        storeAddress,
        sourceType: 'count_complete',
        countSessionId: session.id,
        countSessionName: session.name,
        orderedByName: actor.name,
        orderedByEmail: actor.email,
      };
      const batchResult = await sendPurchaseOrdersBatch(enrichVendorOrdersWithDelivery(), {
        sourceMetadata,
        orderedByName: actor.name,
        orderedByEmail: actor.email,
        markOutsideForMissingEmail: false,
      });
      const poIds = (batchResult.results || [])
        .map((r) => r.purchaseOrderId)
        .filter(Boolean);
      await record('hub_send', {
        vendorCount: vendorData.length,
        poIds,
        sentByEmail: batchResult.sentByEmail,
      });

      // Delete any pre-existing mobile-created draft POs for this session
      const draftPosToDelete = existingPos.filter((po) => po.delivery_status === 'draft');
      if (draftPosToDelete.length > 0) {
        const failedIds = [];
        const deletedIds = [];
        await Promise.all(draftPosToDelete.map(async (draft) => {
          try { await deleteDraftPurchaseOrder(draft.id); deletedIds.push(draft.id); } catch (e) { failedIds.push(draft.id); }
        }));
        if (deletedIds.length > 0) {
          setExistingPos((prev) => prev.filter((po) => !deletedIds.includes(po.id)));
        }
        if (failedIds.length > 0) {
          if (deletedIds.length === 0) {
            setActionError(`Orders sent, but all stale draft(s) could not be cleaned up. Please go to the Drafts tab and delete them manually.`);
          } else {
            setAuditWarning(`Orders sent, but ${failedIds.length} stale draft(s) could not be cleaned up. Check the Drafts tab and delete them manually.`);
          }
        }
      }

      const sentCount = batchResult.sentByEmail + batchResult.pendingApproval + batchResult.sentOutsideCountertop;
      setActionFeedback(`Sent to ${sentCount} vendor${sentCount === 1 ? '' : 's'} successfully.`);
    } catch (err) {
      setActionError(err?.message || 'Send failed.');
    } finally {
      setActionState((s) => ({ ...s, sending: false }));
    }
  };

  const handleGeneratePO = async () => {
    if (!reviewed || isEmpty) return;
    if (interceptWithExistingOrdersModal('generate')) return;
    setActionState((s) => ({ ...s, generating: true }));
    setActionFeedback('');
    setActionError('');
    try {
      const sourceMetadata = {
        storeId,
        storeName,
        storeAddress,
        sourceType: 'count_complete',
        countSessionId: session.id,
        countSessionName: session.name,
        orderedByName: actor.name,
        orderedByEmail: actor.email,
      };
      const purchaseOrders = buildPurchaseOrdersFromReport(enrichVendorOrdersWithDelivery(), sourceMetadata, {
        orderedByName: actor.name,
        orderedByEmail: actor.email,
      });
      await record('hub_generate_po', { vendorCount: purchaseOrders.length });
      purchaseOrders.forEach((po) => printPurchaseOrder(po, storeName, storeAddress));
      setHasPo(true);

      // Delete any pre-existing mobile-created draft POs for this session
      const draftPosToDelete = existingPos.filter((po) => po.delivery_status === 'draft');
      if (draftPosToDelete.length > 0) {
        const failedIds = [];
        const deletedIds = [];
        await Promise.all(draftPosToDelete.map(async (draft) => {
          try { await deleteDraftPurchaseOrder(draft.id); deletedIds.push(draft.id); } catch (e) { failedIds.push(draft.id); }
        }));
        if (deletedIds.length > 0) {
          setExistingPos((prev) => prev.filter((po) => !deletedIds.includes(po.id)));
        }
        if (failedIds.length > 0) {
          if (deletedIds.length === 0) {
            setActionError(`PO generated, but all stale draft(s) could not be cleaned up. Please go to the Drafts tab and delete them manually.`);
          } else {
            setAuditWarning(`PO generated, but ${failedIds.length} stale draft(s) could not be cleaned up. Check the Drafts tab and delete them manually.`);
          }
        }
      }

      setActionFeedback(`Generated ${purchaseOrders.length} PO document${purchaseOrders.length === 1 ? '' : 's'}.`);
    } catch (err) {
      setActionError(err?.message || 'PO generation failed.');
    } finally {
      setActionState((s) => ({ ...s, generating: false }));
    }
  };

  const handlePrintReport = async () => {
    if (!reviewed || isEmpty) return;
    setActionState((s) => ({ ...s, printing: true }));
    setActionFeedback('');
    setActionError('');
    try {
      await record('hub_print');
      printOrderReportWindow(session, vendorData, storeName, actor.name);
      setActionFeedback('Print dialog opened.');
    } catch (err) {
      setActionError(err?.message || 'Print failed.');
    } finally {
      setActionState((s) => ({ ...s, printing: false }));
    }
  };

  const handleCreateDraftOrder = async () => {
    if (!reviewed || isEmpty) return;
    if (interceptWithExistingOrdersModal('draft')) return;
    setActionState((s) => ({ ...s, creatingDraft: true }));
    setActionFeedback('');
    setActionError('');
    try {
      const result = await createDraftPurchaseOrdersFromCount({ countSessionId: session.id });
      const drafts = result?.data?.drafts || [];
      // Refresh associated POs so the button hides once drafts exist.
      // (The edge function already inserts a `draft_created_from_count`
      // activity log entry per vendor; no hub-level event is needed here.)
      const refreshed = await listPurchaseOrdersForCountSession(session.id).catch(() => []);
      setExistingPos(refreshed || []);
      if (drafts.length > 0) {
        // The retry succeeded — clear any stale failure breadcrumb so the
        // "Last attempt failed" note disappears.
        setLastDraftFailure(null);
        const vendorList = drafts.map((d) => d.vendorName).filter(Boolean).join(', ');
        setActionFeedback(
          `Created ${drafts.length} draft order${drafts.length === 1 ? '' : 's'}${vendorList ? ` (${vendorList})` : ''}. They're now in the Drafts tab in Ordering.`
        );
      } else if ((refreshed || []).length > 0) {
        setLastDraftFailure(null);
        setActionFeedback('Draft orders for this count already exist. See the Drafts tab in Ordering.');
      } else {
        const skipped = result?.data?.skipped || [];
        if (skipped.length > 0) {
          // Setup skips (no vendor assigned) are informational; anything else
          // is a server-side failure and must read as one, with its real
          // error — never relabelled as "items need ordering setup".
          const outcome = describeDraftCreationOutcome({ drafts, skipped });
          const hasFailure = skipped.some((entry) => entry && entry.reason !== 'no_vendor_assigned');
          if (hasFailure) setActionError(outcome.message);
          else setActionFeedback(outcome.message);
        } else {
          setActionFeedback('No draft orders were created — there were no orderable items for any vendor.');
        }
      }
    } catch (err) {
      const errMessage = err?.message || 'Failed to create draft order.';
      setActionError(errMessage);
      // Persist this manual-retry failure too so the breadcrumb stays current.
      try {
        await recordOrderingHubEvent({
          action: 'draft_create_failed',
          countSessionId: session.id,
          actorEmail: actor.email,
          details: { error: errMessage, source: 'hub_retry' },
        });
        const refreshed = await fetchLastDraftCreateFailure(session.id).catch(() => null);
        setLastDraftFailure(refreshed || { error: errMessage, occurredAt: new Date().toISOString(), details: {} });
      } catch (_) {
        setLastDraftFailure({ error: errMessage, occurredAt: new Date().toISOString(), details: {} });
      }
    } finally {
      setActionState((s) => ({ ...s, creatingDraft: false }));
    }
  };

  const handleCancel = async () => {
    await record('hub_cancel');
    onCancel();
  };

  const handleEdit = async () => {
    await record('hub_edit');
    window.location.href = `/count-session.html?session=${session.id}`;
  };

  const handleNext = () => onNext();

  const actionsDisabled = !reviewed || loading;

  return (
    <div style={shellStyle}>
      <div style={topBarStyle}>
        <div>
          <div style={sessionTitleStyle}>{session.name || `Session ${session.id}`}</div>
          {isMulti && (
            <div style={progressStyle}>
              Reviewing {reviewIndex + 1} of {reviewTotal}
            </div>
          )}
        </div>
        {isMulti && reviewIndex < reviewTotal - 1 && (
          <button type="button" style={nextBtnStyle} onClick={handleNext} disabled={!reviewed}>
            Next Session →
          </button>
        )}
        {isMulti && reviewIndex === reviewTotal - 1 && (
          <button type="button" style={nextBtnStyle} onClick={handleNext} disabled={!reviewed}>
            Done Reviewing
          </button>
        )}
      </div>

      {loading && <div style={emptyStyle}>Loading order report…</div>}
      {error && <div style={errorBoxStyle}>{error}</div>}

      {!loading && !error && (() => {
        const mobileDrafts = existingPos.filter((po) => po.delivery_status === 'draft');
        const showDraftPicker = mobileDrafts.length > 0 && onOpenDraft && !draftPickerDismissed;

        if (showDraftPicker) {
          return (
            <div style={draftPickerContainerStyle}>
              <div style={draftPickerCardStyle}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>📱</div>
                <div style={{ fontWeight: 700, fontSize: 17, color: '#1F4D6B', marginBottom: 8 }}>
                  {mobileDrafts.length === 1 ? 'A draft order is ready from mobile' : `${mobileDrafts.length} draft orders are ready from mobile`}
                </div>
                <div style={{ fontSize: 14, color: '#2b4a5e', marginBottom: 20, lineHeight: 1.5 }}>
                  This count session already has {mobileDrafts.length === 1 ? 'a draft purchase order' : 'draft purchase orders'} created from the mobile app.
                  You can open and send {mobileDrafts.length === 1 ? 'it' : 'them'} directly, or continue here to generate a new PO from scratch
                  (which will remove the existing mobile draft{mobileDrafts.length === 1 ? '' : 's'} when sent).
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                  {mobileDrafts.map((draft) => (
                    <button
                      key={draft.id}
                      type="button"
                      style={openMobileDraftBtnStyle}
                      onClick={() => onOpenDraft(draft)}
                    >
                      Open Draft{mobileDrafts.length > 1 ? `: ${draft.vendor_name_snapshot || `#${draft.id}`}` : ''} →
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  style={draftPickerDismissBtnStyle}
                  onClick={() => setDraftPickerDismissed(true)}
                >
                  Continue with Web Review instead
                </button>
              </div>
            </div>
          );
        }

        return (
          <>
            <div style={summaryBarStyle}>
              <SumCell label="Vendors" value={String(vendorData.length)} />
              <SumCell label="Order Lines" value={String(totalLines)} />
              <SumCell label="Total Order Value" value={fmtMoney(totalValue)} highlight />
            </div>

            {mobileDrafts.length > 0 && onOpenDraft && (
              <div style={mobileDraftBannerStyle}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1F4D6B', marginBottom: 4 }}>
                  📱 Mobile draft{mobileDrafts.length === 1 ? '' : 's'} still available
                </div>
                <div style={{ fontSize: 12, color: '#2b4a5e', marginBottom: 8 }}>
                  Sending or generating a PO here will remove the mobile draft{mobileDrafts.length === 1 ? '' : 's'}.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {mobileDrafts.map((draft) => (
                    <button
                      key={draft.id}
                      type="button"
                      style={openMobileDraftBtnStyle}
                      onClick={() => onOpenDraft(draft)}
                    >
                      Open Draft{mobileDrafts.length > 1 ? `: ${draft.vendor_name_snapshot || `#${draft.id}`}` : ''} →
                    </button>
                  ))}
                </div>
              </div>
            )}

          <div style={reportBodyStyle}>
            {isEmpty ? (
              <div style={emptyStyle}>
                No order lines to show. This count session had no items with positive order quantities.
              </div>
            ) : (
              vendorData.map((vendor, vIdx) => {
                const info = getVendorDelivery(vendor, vIdx);
                return (
                  <VendorSection
                    key={vendor.vendorId || vendor.vendorName || vIdx}
                    vendor={vendor}
                    requestedDeliveryDate={info.requestedDeliveryDate}
                    deliveryNotes={info.deliveryNotes}
                    onChangeDeliveryDate={(value) => updateVendorDelivery(vendor, vIdx, { requestedDeliveryDate: value })}
                    onChangeDeliveryNotes={(value) => updateVendorDelivery(vendor, vIdx, { deliveryNotes: value })}
                  />
                );
              })
            )}
          </div>

          {actionFeedback && <div style={feedbackStyle}>{actionFeedback}</div>}
          {amendmentDraftIds.length > 0 && (
            <div style={{ margin: '0 28px', padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac', fontSize: 13, color: '#166534' }}>
              <strong>Amendment draft{amendmentDraftIds.length > 1 ? 's' : ''} ready:</strong>
              {amendmentDraftIds.map((d) => {
                const draftPo = existingPos.find((p) => p.id === d.amendmentDraftId) || { id: d.amendmentDraftId, po_number: d.amendmentDraftPoNumber, vendor_name_snapshot: d.vendorName, delivery_status: 'draft' };
                return (
                  <span key={d.amendmentDraftId} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
                    {onOpenDraft ? (
                      <button
                        type="button"
                        style={{ background: 'none', border: 'none', padding: 0, color: '#166534', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', fontSize: 13 }}
                        onClick={() => onOpenDraft(draftPo)}
                      >
                        {d.amendmentDraftPoNumber || `Draft #${d.amendmentDraftId}`}
                        {d.vendorName ? ` (${d.vendorName})` : ''}
                      </button>
                    ) : (
                      <span style={{ fontWeight: 700 }}>{d.amendmentDraftPoNumber || `Draft #${d.amendmentDraftId}`}{d.vendorName ? ` (${d.vendorName})` : ''}</span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
          {actionError && <div style={errorBoxStyle}>{actionError}</div>}
          {auditWarning && (
            <div style={auditWarnStyle}>
              ⚠ {auditWarning}
            </div>
          )}
          {!isEmpty && lastDraftFailure && existingPos.length === 0 && (
            <div style={draftFailureNoteStyle} title={lastDraftFailure.error}>
              <span style={draftFailureBadgeStyle}>Last attempt failed</span>
              <span style={draftFailureMsgStyle}>
                {lastDraftFailure.error}
              </span>
              {lastDraftFailure.occurredAt && (
                <span style={draftFailureWhenStyle}>
                  · {formatFailureWhen(lastDraftFailure.occurredAt)}
                </span>
              )}
            </div>
          )}

          <div style={actionsBarStyle} className="ohub-review-actions">
            <button
              type="button"
              style={cancelBtnStyle}
              className="ohub-action-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              style={editBtnStyle}
              className="ohub-action-btn"
              onClick={handleEdit}
              disabled={actionsDisabled}
            >
              ✏️ Edit
            </button>
            <div style={{ flex: 1 }} className="ohub-action-spacer" />
            {!isEmpty && (
              <button
                type="button"
                style={primaryActionBtnStyle}
                className="ohub-action-btn"
                onClick={handleCreateDraftOrder}
                disabled={actionsDisabled || actionState.creatingDraft}
                title={lastDraftFailure && existingPos.length === 0
                  ? `Last attempt failed: ${lastDraftFailure.error}`
                  : existingPos.length > 0
                    ? 'Existing orders found for this count. Click to review options.'
                    : 'No draft order exists for this count yet. Create one now (e.g. if draft creation failed when the count was completed).'}
              >
                {actionState.creatingDraft ? 'Creating…' : '📝 Create Draft Order'}
              </button>
            )}
            <button
              type="button"
              style={secondaryActionBtnStyle}
              className="ohub-action-btn"
              onClick={handlePrintReport}
              disabled={actionsDisabled || actionState.printing || isEmpty}
            >
              {actionState.printing ? 'Opening…' : '🖨 Print Report'}
            </button>
            <button
              type="button"
              style={hasPo ? secondaryActionBtnStyle : primaryActionBtnStyle}
              className="ohub-action-btn"
              onClick={handleGeneratePO}
              disabled={actionsDisabled || actionState.generating || isEmpty}
            >
              {actionState.generating ? 'Generating…' : '📄 Generate PO'}
            </button>
            {hasPo && (
              <button
                type="button"
                style={primaryActionBtnStyle}
                className="ohub-action-btn"
                onClick={handleSendToVendor}
                disabled={actionsDisabled || actionState.sending || isEmpty}
              >
                {actionState.sending ? 'Sending…' : '📤 Send to Vendor'}
              </button>
            )}
          </div>
        </>
        );
      })()}
      {existingOrdersModal && (() => {
        const { drafts, sentPos } = existingOrdersModal;
        const hasDrafts = drafts.length > 0;
        const hasSent = sentPos.length > 0;
        const actionLabel = existingOrdersModal.pendingAction === 'send'
          ? 'Send to Vendor'
          : existingOrdersModal.pendingAction === 'generate'
            ? 'Generate PO'
            : 'Create Draft Order';
        const fmtLocalDateTime = (iso) => {
          if (!iso) return '';
          try {
            return new Date(iso).toLocaleString(undefined, {
              month: 'short', day: 'numeric', year: 'numeric',
              hour: 'numeric', minute: '2-digit',
            });
          } catch { return ''; }
        };
        const getPoRecipients = (po) => {
          const contact = po.vendor_contact_snapshot || {};
          if (Array.isArray(contact.orderRecipientEmails) && contact.orderRecipientEmails.length > 0) {
            return contact.orderRecipientEmails.filter(Boolean);
          }
          return [contact.contactEmail, contact.generalEmail].filter(Boolean);
        };
        return (
          <div style={existingOrdersModalOverlayStyle}>
            <div style={existingOrdersModalStyle}>
              <div style={existingOrdersModalTitleStyle}>Existing orders found</div>
              <div style={existingOrdersModalBodyStyle}>
                <p style={{ margin: '0 0 10px', color: '#374151', fontSize: 14 }}>
                  Before running <strong>{actionLabel}</strong>, review the existing orders for this count:
                </p>

                {hasDrafts && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={existingOrdersModalSectionHeaderStyle}>
                      Draft{drafts.length > 1 ? 's' : ''} ({drafts.length})
                    </div>
                    {drafts.map((po) => (
                      <div key={po.id} style={existingOrdersModalPoRowStyle}>
                        <span style={{ fontWeight: 600 }}>{po.po_number || `PO #${po.id}`}</span>
                        {po.vendor_name_snapshot && <span style={{ color: '#6b7280', marginLeft: 6 }}>{po.vendor_name_snapshot}</span>}
                        {po.created_at && <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>Created {fmtLocalDateTime(po.created_at)}</span>}
                      </div>
                    ))}
                  </div>
                )}

                {hasSent && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={existingOrdersModalSectionHeaderStyle}>
                      Sent order{sentPos.length > 1 ? 's' : ''} ({sentPos.length})
                    </div>
                    {sentPos.map((po) => {
                      const recipients = getPoRecipients(po);
                      return (
                        <div key={po.id} style={existingOrdersModalPoRowStyle}>
                          <div style={{ width: '100%', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
                            <span style={{ fontWeight: 600 }}>{po.po_number || `PO #${po.id}`}</span>
                            {po.vendor_name_snapshot && <span style={{ color: '#6b7280', marginLeft: 6 }}>{po.vendor_name_snapshot}</span>}
                            <span style={{ marginLeft: 6, fontSize: 11, background: '#fef3c7', color: '#92400e', borderRadius: 4, padding: '1px 5px', textTransform: 'capitalize' }}>{po.delivery_status}</span>
                          </div>
                          {po.sent_at && (
                            <div style={{ width: '100%', color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                              Sent {fmtLocalDateTime(po.sent_at)}
                              {recipients.length > 0 && <span> · {recipients.join(', ')}</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {existingOrdersError && (
                  <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8, padding: '6px 10px', background: '#fef2f2', borderRadius: 6, border: '1px solid #fca5a5' }}>
                    {existingOrdersError}
                  </div>
                )}
              </div>

              <div style={existingOrdersModalActionsStyle}>
                <button
                  type="button"
                  style={{ ...BTN_SECONDARY, minWidth: 80 }}
                  onClick={() => { setExistingOrdersModal(null); setExistingOrdersError(''); }}
                  disabled={existingOrdersWorking}
                >
                  Cancel
                </button>

                {hasDrafts && hasSent && (
                  <button
                    type="button"
                    style={{ ...BTN_PRIMARY }}
                    onClick={() => handleExistingOrdersConfirm('both')}
                    disabled={existingOrdersWorking}
                  >
                    {existingOrdersWorking ? 'Updating…' : `Update ${drafts.length} Draft${drafts.length > 1 ? 's' : ''} & Amend ${sentPos.length} Sent`}
                  </button>
                )}

                {hasDrafts && !hasSent && (
                  <button
                    type="button"
                    style={{ ...BTN_PRIMARY }}
                    onClick={() => handleExistingOrdersConfirm('update')}
                    disabled={existingOrdersWorking}
                  >
                    {existingOrdersWorking ? 'Updating…' : `Update Draft${drafts.length > 1 ? 's' : ''} with Current Count`}
                  </button>
                )}

                {hasSent && !hasDrafts && (
                  <button
                    type="button"
                    style={{ ...BTN_PRIMARY }}
                    onClick={() => handleExistingOrdersConfirm('amend')}
                    disabled={existingOrdersWorking}
                  >
                    {existingOrdersWorking ? 'Creating Amendment…' : `Amend Sent Order${sentPos.length > 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
      <style>{mobileCss}</style>
    </div>
  );
}

function VendorSection({
  vendor,
  requestedDeliveryDate = '',
  deliveryNotes = '',
  onChangeDeliveryDate,
  onChangeDeliveryNotes,
}) {
  const items = vendor.items || [];
  const contactLine = [
    vendor.contact?.contactName,
    vendor.contact?.contactEmail,
    vendor.contact?.contactPhone,
  ].filter(Boolean).join(' · ');

  return (
    <div style={vendorCardStyle}>
      <div style={vendorHeadStyle}>
        <div>
          <div style={vendorNameStyle}>🚚 {vendor.vendorName || 'Unknown vendor'}</div>
          {contactLine ? <div style={vendorContactStyle}>{contactLine}</div> : null}
          {!vendor.canSend && vendor.sendReadinessReason ? (
            <div style={warnStyle}>{vendor.sendReadinessReason}</div>
          ) : null}
        </div>
        <div style={vendorTotalStyle}>
          {fmtMoney(vendor.totalOrderValue)}
          {vendor.unpricedCount > 0 ? (
            <div style={{ fontSize: 11, fontWeight: 600, color: '#8a5a2b' }}>
              {`${vendor.unpricedCount} line${vendor.unpricedCount === 1 ? '' : 's'} still need${vendor.unpricedCount === 1 ? 's' : ''} a price`}
            </div>
          ) : null}
        </div>
      </div>
      {(onChangeDeliveryDate || onChangeDeliveryNotes) && (
        <div style={vendorDeliveryRowStyle}>
          <div style={{ flex: '1 1 180px', minWidth: 160 }}>
            <label style={vendorDeliveryLabelStyle}>
              Requested Delivery Date
            </label>
            <input
              type="date"
              value={requestedDeliveryDate || ''}
              onChange={(e) => onChangeDeliveryDate && onChangeDeliveryDate(e.target.value)}
              style={vendorDeliveryInputStyle}
            />
          </div>
          <div style={{ flex: '2 1 240px', minWidth: 180 }}>
            <label style={vendorDeliveryLabelStyle}>
              Delivery Notes
            </label>
            <textarea
              value={deliveryNotes || ''}
              onChange={(e) => onChangeDeliveryNotes && onChangeDeliveryNotes(e.target.value)}
              maxLength={300}
              rows={2}
              style={{ ...vendorDeliveryInputStyle, resize: 'vertical' }}
              placeholder="e.g. Back door delivery, call before arrival"
            />
          </div>
        </div>
      )}
      <div style={itemsStyle}>
        <div style={itemHeadRowStyle} className="ohub-orr-row ohub-orr-head">
          <span className="ohub-orr-desc" style={{ ...itemDescColStyle, color: '#5A4A42', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Item</span>
          <span className="ohub-orr-num" data-label="On Hand" style={{ ...itemNumColStyle, color: '#5A4A42', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>On Hand</span>
          <span className="ohub-orr-num" data-label="PAR" style={{ ...itemNumColStyle, color: '#5A4A42', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>PAR</span>
          <span className="ohub-orr-num" data-label="Order" style={{ ...itemNumColStyle, color: '#5A4A42', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Order</span>
          <span className="ohub-orr-num" data-label="Unit $" style={{ ...itemNumColStyle, color: '#5A4A42', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Unit $</span>
          <span className="ohub-orr-num" data-label="Total" style={{ ...itemNumColStyle, color: '#5A4A42', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Total</span>
        </div>
        {items.map((item, iIdx) => (
          <div key={iIdx} style={itemRowStyle} className="ohub-orr-row">
            <span className="ohub-orr-desc" style={itemDescColStyle}>
              {item.description}
              {item.brand ? <span style={itemSubStyle}> · {item.brand}</span> : null}
              {item.location && item.location !== '-' ? <span style={itemSubStyle}> · {item.location}</span> : null}
            </span>
            <span className="ohub-orr-num" data-label="On Hand" style={itemNumColStyle}>{fmtQtyUnit(item.quantity, item.countUnit || item.unit)}</span>
            <span className="ohub-orr-num" data-label="PAR" style={itemNumColStyle}>{fmtQtyUnit(item.parLevel, item.countUnit || item.unit)}</span>
            <span className="ohub-orr-num" data-label="Order" style={itemNumColStyle}>{fmtQtyUnit(item.orderQuantity, item.orderUnit || item.unit)}</span>
            <span
              className="ohub-orr-num"
              data-label="Unit $"
              style={item.priceAvailable === false ? { ...itemNumColStyle, color: '#8a5a2b', fontWeight: 700 } : itemNumColStyle}
              title={item.priceAvailable === false ? 'No price on record for this line. Enter one in Draft Order Review before sending.' : undefined}
            >
              {item.priceAvailable === false
                ? PRICE_UNAVAILABLE
                : `${fmtMoney(item.unitPrice)}/${item.orderUnit || item.unit || 'unit'}`}
            </span>
            <span className="ohub-orr-num" data-label="Total" style={itemNumColStyle}>{fmtMoneyOrMissing(item.orderValue)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SumCell({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#8B7B73', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700, fontSize: 18, color: highlight ? '#526B7C' : '#3D2518', marginTop: 2 }}>{value}</div>
    </div>
  );
}

function printOrderReportWindow(session, vendorData, storeName, createdBy) {
  const fmt = (n) => Number.isFinite(Number(n)) ? Number(n).toLocaleString(undefined, { style: 'currency', currency: 'USD' }) : '$0.00';
  const fmtN = (n) => Number.isFinite(Number(n)) ? String(Number.isInteger(Number(n)) ? Number(n) : Number(n).toFixed(2)) : '0';
  const fmtDate = (d) => { try { return new Date(`${d}T00:00:00`).toLocaleDateString(); } catch { return String(d); } };
  const total = vendorData.reduce((s, v) => s + (Number(v.totalOrderValue) || 0), 0);

  const rawCountDates = [...new Set(
    vendorData.flatMap((v) => (v.items || []).map((it) => it.countDate)).filter(Boolean)
  )].sort();
  const countDates = rawCountDates.map(fmtDate);

  const counters = [...new Set(
    vendorData.flatMap((v) => (v.items || []).map((it) => it.countedByName || it.countedBy)).filter(Boolean)
  )];

  const cols = [
    { key: 'description', label: 'Item', align: 'text' },
    { key: 'location', label: 'Location', align: 'text', width: '90px' },
    { key: 'onHand', label: 'On Hand', align: 'number', width: '56px' },
    { key: 'par', label: 'PAR', align: 'number', width: '56px' },
    { key: 'order', label: 'Order', align: 'number', width: '56px' },
    { key: 'unitPrice', label: 'Unit $', align: 'currency', width: '72px' },
    { key: 'total', label: 'Total', align: 'currency', width: '80px' },
  ];
  const sections = vendorData.map((v) => ({
    title: v.vendorName,
    sectionNote: v.sendReadinessReason || undefined,
    columns: cols,
    rows: (v.items || []).map((it) => ({
      description: it.description + (it.brand ? ` \u2014 ${it.brand}` : ''),
      location: it.location || '',
      onHand: fmtN(it.quantity),
      par: fmtN(it.parLevel),
      order: fmtN(it.orderQuantity),
      unitPrice: it.priceAvailable === false ? 'Price needed' : fmt(it.unitPrice),
      total: it.priceAvailable === false ? 'Price needed' : fmt(it.orderValue),
    })),
    totals: { description: 'Vendor Total', total: fmt(v.totalOrderValue) },
  }));
  const descriptor = {
    store: { name: storeName || '' },
    report: {
      title: 'Count Order Review',
      subtitle: session.name || 'Session',
      counters,
      countDates,
      createdBy: createdBy || '',
      generatedAt: new Date().toISOString(),
      meta: [{ label: 'Session', value: session.name || '' }],
    },
    stats: [
      { label: 'Vendors', value: String(vendorData.length) },
      { label: 'Order Total', value: fmt(total) },
    ],
    sections,
  };
  printReportDocument(descriptor);
}

const shellStyle = {
  fontFamily: 'Open Sans, sans-serif',
  color: '#3D2518',
  maxWidth: 1100,
  margin: '0 auto',
  padding: '0 0 32px',
};
const topBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '18px 28px',
  background: '#fdfaf6',
  borderBottom: '1px solid #E9DFD6',
};
const sessionTitleStyle = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 800,
  fontSize: 20,
  color: '#3D2518',
};
const progressStyle = {
  fontSize: 13,
  color: '#8B7B73',
  marginTop: 4,
};
const nextBtnStyle = { ...BTN_PRIMARY };
const summaryBarStyle = {
  display: 'flex',
  gap: 24,
  padding: '14px 28px',
  borderBottom: '1px solid #E9DFD6',
  background: '#fff',
};
const reportBodyStyle = {
  padding: '16px 28px',
  maxHeight: '55vh',
  overflowY: 'auto',
};
const emptyStyle = {
  padding: 32,
  textAlign: 'center',
  color: '#8B7B73',
};
const vendorCardStyle = {
  border: '1px solid #E9DFD6',
  borderRadius: 10,
  marginBottom: 12,
  overflow: 'hidden',
};
const vendorHeadStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '10px 14px',
  background: '#f5ede3',
  borderBottom: '1px solid #E9DFD6',
};
const vendorNameStyle = { fontWeight: 700, color: '#3D2518', fontSize: 15 };
const vendorContactStyle = { color: '#8B7B73', fontSize: 12, marginTop: 3 };
const warnStyle = { color: '#bf360c', fontSize: 12, marginTop: 3, fontWeight: 600 };
const vendorTotalStyle = { fontWeight: 700, color: '#3D2518', fontSize: 15, whiteSpace: 'nowrap' };
const itemsStyle = { padding: '6px 10px' };
const vendorDeliveryRowStyle = {
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  padding: '10px 14px',
  background: '#fbf6f0',
  borderBottom: '1px solid #efe5d9',
};
const vendorDeliveryLabelStyle = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#5A4A42',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};
const vendorDeliveryInputStyle = {
  width: '100%',
  border: '1px solid #d9cfc7',
  borderRadius: 6,
  padding: '7px 10px',
  fontSize: 13,
  color: '#2b1d14',
  background: '#fff',
  boxSizing: 'border-box',
};
const itemHeadRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2.4fr) repeat(5, minmax(0, 1fr))',
  gap: 8,
  padding: '4px 6px',
  borderBottom: '1px solid #E9DFD6',
};
const itemRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2.4fr) repeat(5, minmax(0, 1fr))',
  gap: 8,
  padding: '5px 6px',
  borderBottom: '1px solid #f6efe6',
  fontSize: 13,
  color: '#5A4A42',
};
const itemDescColStyle = { minWidth: 0, wordBreak: 'break-word' };
const itemNumColStyle = { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
const itemSubStyle = { color: '#8B7B73', fontSize: 12 };
const actionsBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 10,
  padding: '14px 28px',
  paddingBottom: 'max(14px, calc(14px + env(safe-area-inset-bottom, 0px)))',
  borderTop: '1.5px solid #E9DFD6',
  background: '#fff',
  alignItems: 'center',
  flexShrink: 0,
};
const cancelBtnStyle = { ...BTN_SECONDARY, fontWeight: 600 };
const editBtnStyle = { ...BTN_SECONDARY, fontWeight: 600 };
const secondaryActionBtnStyle = { ...BTN_SECONDARY };
const primaryActionBtnStyle = { ...BTN_PRIMARY };
const draftPickerContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  padding: '40px 28px',
};
const draftPickerCardStyle = {
  background: '#E3F0FF',
  border: '2px solid #93C5E8',
  borderRadius: 14,
  padding: '32px 28px',
  maxWidth: 480,
  width: '100%',
  textAlign: 'center',
};
const draftPickerDismissBtnStyle = {
  background: 'transparent',
  border: '1.5px solid #93C5E8',
  color: '#2b4a5e',
  padding: '9px 18px',
  borderRadius: 8,
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
  marginTop: 4,
};
const mobileDraftBannerStyle = {
  margin: '12px 28px 0',
  padding: '14px 16px',
  background: '#E3F0FF',
  border: '1.5px solid #93C5E8',
  borderRadius: 10,
  color: '#1F4D6B',
};
const openMobileDraftBtnStyle = {
  background: '#1F4D6B',
  color: '#fff',
  border: 'none',
  padding: '8px 16px',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: 13,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};
const feedbackStyle = {
  margin: '0 28px',
  padding: '10px 14px',
  background: '#e8f5e9',
  borderRadius: 8,
  color: '#1b5e20',
  fontSize: 13,
  fontWeight: 600,
};
const errorBoxStyle = {
  margin: '0 28px',
  padding: '10px 14px',
  background: '#fdecea',
  borderRadius: 8,
  color: '#b71c1c',
  fontSize: 13,
};
const auditWarnStyle = {
  margin: '0 28px',
  padding: '10px 14px',
  background: '#fff8e1',
  borderRadius: 8,
  color: '#e65100',
  fontSize: 12,
  border: '1px solid #ffcc02',
};
const draftFailureNoteStyle = {
  margin: '8px 28px 0',
  padding: '10px 14px',
  background: '#fdecea',
  borderRadius: 8,
  border: '1px solid #f5c6c0',
  color: '#b71c1c',
  fontSize: 13,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
};
const draftFailureBadgeStyle = {
  background: '#b71c1c',
  color: '#fff',
  borderRadius: 999,
  padding: '2px 9px',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};
const draftFailureMsgStyle = {
  flex: '1 1 auto',
  minWidth: 0,
  overflowWrap: 'anywhere',
  fontWeight: 500,
};
const draftFailureWhenStyle = {
  color: '#8a3a36',
  fontSize: 12,
  fontWeight: 500,
};

const existingOrdersModalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(0,0,0,0.45)',
  padding: '16px',
};
const existingOrdersModalStyle = {
  background: '#fff',
  borderRadius: 12,
  boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
  width: '100%',
  maxWidth: 520,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};
const existingOrdersModalTitleStyle = {
  padding: '18px 22px 12px',
  fontWeight: 700,
  fontSize: 18,
  color: '#1f2937',
  borderBottom: '1px solid #e5e7eb',
};
const existingOrdersModalBodyStyle = {
  padding: '16px 22px',
  overflowY: 'auto',
  maxHeight: 380,
};
const existingOrdersModalSectionHeaderStyle = {
  fontWeight: 700,
  fontSize: 12,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom: 6,
};
const existingOrdersModalPoRowStyle = {
  padding: '6px 10px',
  background: '#f9fafb',
  borderRadius: 6,
  marginBottom: 4,
  fontSize: 13,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 4,
};
const existingOrdersModalActionsStyle = {
  padding: '14px 22px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  borderTop: '1px solid #e5e7eb',
  background: '#f9fafb',
};

const mobileCss = `
  ${FOCUS_RING_CSS}
  @media (max-width: 640px) {
  .ohub-review-actions {
    padding: 12px 16px !important;
    padding-bottom: max(12px, calc(12px + env(safe-area-inset-bottom, 0px))) !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 8px !important;
    position: sticky !important;
    bottom: 0 !important;
    z-index: 10 !important;
    box-shadow: 0 -2px 12px rgba(43,29,20,0.08) !important;
  }
  .ohub-review-actions button {
    width: 100% !important;
    min-height: 44px !important;
    justify-content: center !important;
    text-align: center !important;
  }
  .ohub-review-actions .ohub-action-spacer { display: none !important; }
  .ohub-orr-row {
    grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
    gap: 4px 8px !important;
    padding: 8px 6px !important;
  }
  .ohub-orr-row .ohub-orr-desc {
    grid-column: 1 / -1 !important;
    font-weight: 600 !important;
    color: #3D2518 !important;
    font-size: 13px !important;
    text-transform: none !important;
    letter-spacing: 0 !important;
  }
  .ohub-orr-head .ohub-orr-desc { display: none !important; }
  .ohub-orr-head { padding: 4px 6px !important; }
  .ohub-orr-row .ohub-orr-num {
    font-size: 12px !important;
    text-align: center !important;
    display: flex !important;
    flex-direction: column;
    align-items: center;
  }
  .ohub-orr-head .ohub-orr-num { display: block !important; text-align: center !important; }
  .ohub-orr-row:not(.ohub-orr-head) .ohub-orr-num::before {
    content: attr(data-label);
    color: #8B7B73;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 2px;
  }
}
`;
